// THE WORD FOR "HOSPITAL", per tenant kind.
//
// Owner, 30 Sep 2026: a tenant has a TYPE chosen by the Super Admin — Hospital,
// Clinic or MOC — and the words on screen must follow it. Tenant 1 ("Mitra One
// Care", kind 'moc') is not a hospital, yet the locale files say "hospital" well
// over a hundred times. Rather than rewrite every string into a variable, every
// translation goes through orgText() (an i18next post-processor, see i18n.js),
// which swaps the noun — and, for 'moc', its determiner — for the right words.
//
// The mobile twin is Mitra-OneCare-Pro-App/src/orgWords.js: SAME RULES. A change
// here is a change there.
//
//   kind 'hospital' / unknown / no org → the text unchanged (an old server).
//   kind 'clinic'        → clinic(s)        · FR établissement(s)
//   kind 'organisation'  → organisation(s)  · FR établissement(s)
//   kind 'moc'           → the tenant's NAME replaces the noun AND its
//                          determiner ("this hospital" → "Mitra One Care",
//                          "de l'hôpital" → "de Mitra One Care"); plurals and
//                          generic uses ("another hospital", "each hospital")
//                          take the organisation words. No name → organisation.
//
// French uses "établissement", never "clinique": it is masculine and starts with
// a vowel, exactly like "hôpital", so "cet", "l'", "d'", "un" all stay right.
//
// Whole words only ("hospitalisation", "admin@hospital.com", "hospital_sets"
// are left alone); the first letter's capitalisation is kept.

const KINDS = new Set(['hospital', 'clinic', 'organisation', 'moc']);

const GENERIC = {
    clinic: { en: 'clinic', enPl: 'clinics', fr: 'établissement', frPl: 'établissements' },
    organisation: { en: 'organisation', enPl: 'organisations', fr: 'établissement', frPl: 'établissements' },
};

// Determiners that point at ONE particular hospital — for 'moc' they are eaten
// together with the noun: "this hospital" is simply "Mitra One Care".
const EN_SPECIFIC = new Set(['this', 'the', 'your', 'our', 'their', 'its', 'my', 'that', 'a', 'an']);
// Words that make the sentence about hospitals in general, not this one:
// "another hospital" must never become "another Mitra One Care".
const EN_GENERIC_DETS = 'another|other|each|every|any|per|which|same|all|both|several|many|different|some|connected|two|referral|partner|specific|particular|single';
// "no" goes either way: "No hospital sets yet" is this one's sets ("No Mitra
// One Care sets yet"), "No hospital vouches for…" is any hospital at all.
const EN_DETS = `this|the|your|our|their|its|my|that|an|a|no|${EN_GENERIC_DETS}`;

// "the hospital admin" is the admin OF Mitra One Care — keep the article:
// "the Mitra One Care admin", not "Mitra One Care admin".
const EN_ATTRIBUTIVE = new Set([
    'admin', 'admins', 'administrator', 'administrators', 'staff', 'team', 'desk', 'desks',
    'settings', 'account', 'accounts', 'set', 'sets', 'programme', 'programmes', 'code',
    'codes', 'number', 'numbers', 'page', 'policy', 'profile', 'prefix', 'record',
    'records', 'rules', 'zone', 'catalogue', 'catalog', 'pack', 'check', 'checks',
    'doctor', 'doctors', 'plan', 'note', 'payment', 'payments', 'web', 'app', 'list',
]);

const FR_SPECIFIC_WORDS = new Set(['cet', 'un', 'votre', 'notre', 'leur', 'son', 'mon', 'ton']);
const FR_GENERIC_WORDS = 'autre|chaque|aucun|tout|quel|même|par|plusieurs|les|des|aux|ces|vos|nos|leurs|deux';
const FR_DETS = `d['’]un\\s+|d['’]|l['’]|(?:cet|un|votre|notre|leur|son|mon|ton|${FR_GENERIC_WORDS})\\s+`;

// Not preceded by a letter/digit/underscore, nor by @ . / \ { (e-mail, domain,
// path, {{placeholder}}); not followed by a letter/digit/underscore/@/}, nor by
// ".x" (domain).
const BEFORE = '(?<![\\p{L}\\p{N}_@./\\\\{])';
const AFTER = '(?![\\p{L}\\p{N}_@}]|\\.[\\p{L}\\p{N}])';

const EN_RE = new RegExp(`${BEFORE}(?:(${EN_DETS})(\\s+|-))?(hospital)(s)?${AFTER}`, 'giu');
// "dans" is captured so that, for 'moc', "dans cet hôpital" reads "chez
// Mitra One Care". "établissement" is matched too: many French strings already
// use it as the neutral word, and for 'moc' it is the tenant as well — for
// every other kind it is left exactly as it is.
const FR_RE = new RegExp(`${BEFORE}(?:(dans)\\s+)?(${FR_DETS})?(h[ôo]pital|h[ôo]pitaux|[ée]tablissements?)${AFTER}`, 'giu');

const isUpper = (s) => s === s.toUpperCase() && s !== s.toLowerCase();

// The replacement takes the capitalisation of the word it replaces.
const withCase = (source, out) => {
    if (source.length > 1 && isUpper(source)) return out.toUpperCase();
    if (isUpper(source[0])) return out[0].toUpperCase() + out.slice(1);
    return out;
};

const startsWithVowel = (s) => /^[aeiouàâäéèêëîïôöùûüœæ]/i.test(s || '');

/** The org as this module understands it, or null for "say hospital". */
export function normaliseOrg(org) {
    if (!org || typeof org !== 'object') return null;
    let kind = typeof org.kind === 'string' ? org.kind.trim().toLowerCase() : '';
    if (kind === 'organization') kind = 'organisation';
    if (!KINDS.has(kind) || kind === 'hospital') return null;
    const name = typeof org.name === 'string' ? org.name.trim() : '';
    if (kind === 'moc' && !name) return { kind: 'organisation', name: '' };
    return { kind, name };
}

function english(text, org) {
    const generic = GENERIC[org.kind] || GENERIC.organisation;
    const isMoc = org.kind === 'moc';
    return text.replace(EN_RE, (match, det, sep, word, plural, offset, whole) => {
        if (plural) {
            const noun = withCase(word, generic.enPl);
            return det ? `${det}${sep}${noun}` : noun;
        }
        const detLower = det ? det.toLowerCase() : '';
        const next = /^\s+([\p{L}]+)/u.exec(whole.slice(offset + match.length));
        const attributive = !!(det && /\s/.test(sep) && next && EN_ATTRIBUTIVE.has(next[1].toLowerCase()));
        const specific = !det || EN_SPECIFIC.has(detLower) || (detLower === 'no' && attributive);
        if (isMoc && specific) {
            if (attributive) {
                let keep = det;
                if (detLower === 'a' || detLower === 'an') {
                    keep = withCase(det, startsWithVowel(org.name) ? 'an' : 'a');
                }
                return `${keep}${sep}${org.name}`;
            }
            return org.name;
        }
        const noun = withCase(word, generic.en);
        if (!det) return noun;
        if (detLower === 'a' || detLower === 'an') {
            return `${withCase(det, startsWithVowel(generic.en) ? 'an' : 'a')}${sep}${noun}`;
        }
        return `${det}${sep}${noun}`;
    });
}

function french(text, org) {
    const generic = GENERIC[org.kind] || GENERIC.organisation;
    const isMoc = org.kind === 'moc';
    const out = text.replace(FR_RE, (match, dans, det, word) => {
        const lead = `${dans ? `${dans} ` : ''}${det || ''}`;
        const neutral = /^[ée]/i.test(word);
        const plural = /(ux|s)$/i.test(word);
        if (neutral && !isMoc) return match; // "établissement" is already right
        if (plural) return `${lead}${neutral ? word : withCase(word, generic.frPl)}`;
        const detWord = det ? det.trim().toLowerCase() : '';
        const elidedDe = /^d['’]/.test(detWord);
        const specific = !det || elidedDe || /^l['’]$/.test(detWord) || FR_SPECIFIC_WORDS.has(detWord);
        if (isMoc && specific) {
            const inside = dans ? `${withCase(dans, 'chez')} ` : '';
            if (elidedDe) {
                // "d'hôpital" / "d'un hôpital" → "de Mitra One Care" ("d'Apollo").
                const apostrophe = det.includes('’') ? '’' : "'";
                const d = det[0];
                return inside + (startsWithVowel(org.name) ? `${d}${apostrophe}${org.name}` : `${d}e ${org.name}`);
            }
            // "dans cet hôpital" → "chez Mitra One Care".
            return inside + org.name;
        }
        return `${lead}${neutral ? word : withCase(word, generic.fr)}`;
    });
    if (!isMoc || !startsWithVowel(org.name)) return out;
    // "de l'hôpital" became "de Apollo" — French elides: "d'Apollo".
    const escaped = org.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return out.replace(new RegExp(`(?<![\\p{L}])([dD]|[qQ]u)e (?=${escaped})`, 'gu'), '$1’');
}

/**
 * The text with "hospital" in the words of the signed-in tenant's kind.
 *
 * @param {string} text  a translated string (already interpolated)
 * @param {{kind?: string, name?: string|null}|null} org  from /tenants/my-features
 * @param {string} [lang] the UI language. English rules always run (a French
 *   screen can show an English default text); French rules run for 'fr' or
 *   whenever the text holds "hôpital".
 * @param {string[]} [keep] substrings left exactly as they are — interpolated
 *   values such as a hospital's own name ("Apollo Hospital").
 */
export function orgText(text, org, lang, keep) {
    if (typeof text !== 'string' || !text) return text;
    const o = normaliseOrg(org);
    if (!o) return text;
    const runFrench = /^fr/i.test(lang || '') || /h[ôo]pita/i.test(text);
    // "établissement" is only rewritten on a French screen (for 'moc').
    if (!/hospital/i.test(text) && !runFrench) return text;

    // Mask the values that must survive untouched.
    const masks = [];
    let s = text;
    for (const k of keep || []) {
        if (typeof k !== 'string' || !k || !/hospital|h[ôo]pita/i.test(k) || !s.includes(k)) continue;
        const token = `${masks.length}`;
        masks.push([token, k]);
        s = s.split(k).join(token);
    }

    s = english(s, o);
    if (runFrench) s = french(s, o);

    for (const [token, k] of masks) s = s.split(token).join(k);
    return s;
}

// ---- The signed-in tenant's org (module-level holder) ----------------------
// Set when /tenants/my-features answers (useTenantFeatures), cleared at sign-
// out. i18n.js listens and re-renders the text already on screen.

let currentOrg = null;
const listeners = new Set();

const sameOrg = (a, b) =>
    (a === null && b === null) ||
    (a !== null && b !== null && a.kind === b.kind && a.name === b.name && a.typeName === b.typeName);

export function setCurrentOrg(org) {
    const next = org && typeof org === 'object'
        ? {
            kind: typeof org.kind === 'string' ? org.kind : null,
            name: typeof org.name === 'string' ? org.name : null,
            typeName: typeof org.typeName === 'string' ? org.typeName : null,
        }
        : null;
    if (sameOrg(currentOrg, next)) return;
    currentOrg = next;
    listeners.forEach((fn) => { try { fn(currentOrg); } catch { /* a listener's problem */ } });
}

export const getCurrentOrg = () => currentOrg;

export function subscribeOrg(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
}

// ---- The i18next post-processor (registered in i18n.js) --------------------
// Runs on every translation, the t('key', 'Default text') form included.
//
// Interpolated values are passed through untouched: "{{hospital}}" filled with
// "Apollo Hospital" is a name, not the word.
//
// The public marketing pages talk about hospitals in general ("Run your
// hospital, clinic or practice"), and the Super Admin's tenant pages about
// tenants of every kind — those keys are never rewritten.
export const ORG_WORDS_SKIP = ['home.', 'publicPages.', 'tenantpage.', 'doctorReview.', 'settings.none_for_super_admin'];
// Single keys whose word is a REAL place, never the signed-in tenant: a
// doctor's place of study (fr "Établissement"), the hospital a shared record
// came from, and the Super Admin's tenant picker on the admin page.
export const ORG_WORDS_LITERAL = new Set([
    'doctorprofiletab.institute', 'doctorprofiletab.institutetext',
    'doctorprofiletab.instituterequired', 'doctorprofiletab.instituteexceed',
    'healthRecord.details.hospital', 'sharedVisits.hospital',
    'adminpage.tenant', 'adminpage.select_tenant', 'adminpage.tenant_required',
    'adminpage.tenant_hint', 'adminpage.search_tenant',
]);
const I18N_OPTION_KEYS = new Set(['lng', 'lngs', 'ns', 'context', 'keyPrefix', 'postProcess', 'fallbackLng', 'interpolation']);

const interpolatedValues = (options) => {
    const out = [];
    const walk = (obj) => {
        if (!obj || typeof obj !== 'object') return;
        for (const [k, v] of Object.entries(obj)) {
            if (I18N_OPTION_KEYS.has(k) || k.startsWith('defaultValue')) continue;
            if (typeof v === 'string') out.push(v);
        }
    };
    walk(options);
    walk(options?.replace);
    return out;
};

export const orgWordsPostProcessor = {
    type: 'postProcessor',
    name: 'orgWords',
    process(value, key, options, translator) {
        const org = currentOrg;
        if (!org || typeof value !== 'string') return value;
        const k = Array.isArray(key) ? key[0] : key;
        if (typeof k === 'string' && (ORG_WORDS_LITERAL.has(k) || ORG_WORDS_SKIP.some((p) => k.startsWith(p)))) return value;
        const lang = options?.lng || translator?.language;
        return orgText(value, org, lang, interpolatedValues(options));
    },
};

// localStorage key the org is cached under, so a reload reads right at once.
// localStorage.clear() at logout removes it with everything else.
export const ORG_CACHE_KEY = 'tenantOrg';
